import { useState, useCallback } from "react";
import { toast } from "material-react-toastify";

import { resolveScanner, scanDriversLicense } from "./scanDriversLicense";
import { toGuestFields } from "./guestFieldMapping";
import { clearPreferredScanner } from "./scannerPreference";
import { ScannerError } from "./errors";

/**
 * Drives an AmbirScan Web Connect scan and hands the caller parsed fields and
 * images. Each screen supplies its own sinks, so this hook never knows whether
 * the form underneath is formik or useState.
 *
 * @param {object} params
 * @param {(fields: object) => void} params.onFields called only when a licence was decoded
 * @param {(images: {frontImage: string|null, backImage: string|null}) => void} params.onImages
 *        called whenever the scanner returned pages, decoded or not
 */
export function useAmbirScanWebConnect({ onFields, onImages }) {
  const [isScanning, setIsScanning] = useState(false);
  const [choices, setChoices] = useState([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const runScan = useCallback(
    async (scannerName) => {
      setIsScanning(true);
      try {
        const result = await scanDriversLicense(scannerName);

        if (onImages) {
          onImages({
            frontImage: result.frontImage,
            backImage: result.backImage,
          });
        }

        if (!result.license) {
          toast.warning(
            "ID images captured, but the licence barcode couldn't be read. Please enter the details manually."
          );
          return;
        }

        if (onFields) {
          // Some screens supply an async sink (e.g. one that awaits
          // getUserId(...) to persist the scanned images before returning).
          // Awaiting it here — inside this try — means a rejection is
          // caught below instead of becoming an unhandled promise
          // rejection while the code below unconditionally reports success.
          // The sink's own failure is distinguished from a scanner-layer
          // failure with its own message and an early return, so it never
          // reaches the generic "Failed to scan the ID" wording below (that
          // would misreport a downstream save failure as a scan failure)
          // and never clears the scanner preference (this has nothing to
          // do with which device was used).
          try {
            await Promise.resolve(onFields(toGuestFields(result.license)));
          } catch (sinkError) {
            console.error(
              "[ambirScanWebConnect] onFields sink failed",
              sinkError
            );
            toast.error(
              "ID scanned, but saving the details failed. Please re-enter them manually."
            );
            return;
          }
        }
        toast.success("ID scanned. Please verify the details.");
      } catch (error) {
        toast.error(
          error instanceof ScannerError
            ? error.message
            : "Failed to scan the ID. Please try again."
        );
        // A stored preference points `resolveScanner` straight at a device,
        // short-circuiting the picker. If that device was the wrong one
        // (e.g. a desk once picked "Microsoft Print to PDF" — exactly the
        // case the picker exists to prevent), the button fails forever with
        // no way back into the picker short of devtools. Clear it on any
        // device/service-level failure so the picker reappears next time;
        // SCAN_IN_PROGRESS is transient and unrelated to device choice, so
        // it is excluded.
        if (error instanceof ScannerError && error.code !== "SCAN_IN_PROGRESS") {
          clearPreferredScanner();
        }
      } finally {
        setIsScanning(false);
      }
    },
    [onFields, onImages]
  );

  const startScan = useCallback(async () => {
    setIsScanning(true);
    let resolved;
    try {
      resolved = await resolveScanner();
    } catch (error) {
      toast.error(
        error instanceof ScannerError
          ? error.message
          : "Could not reach the scanner service."
      );
      setIsScanning(false);
      return;
    }

    if (resolved.status === "choose") {
      setChoices(resolved.choices);
      setIsPickerOpen(true);
      setIsScanning(false);
      return;
    }

    // Deliberately not clearing isScanning here: React 17 has no automatic
    // batching outside event handlers, so a `finally` here would commit a
    // false render before `runScan`'s own `setIsScanning(true)` commits,
    // re-enabling the scan button for a moment mid-flow. runScan's own
    // try/finally owns the false-transition for this path — do not "tidy"
    // a finally back in here.
    await runScan(resolved.name);
  }, [runScan]);

  const pickerProps = {
    open: isPickerOpen,
    choices,
    onCancel: () => setIsPickerOpen(false),
    onSelect: (name) => {
      setIsPickerOpen(false);
      runScan(name);
    },
  };

  return { startScan, isScanning, pickerProps };
}
